import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { Button } from 'foundations-components/transitional/components/Button';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PortalIdParser from 'PortalIdParser';
import { useMemo } from 'react';
import { useObjectId } from '../../../v2/hooks/useObjectId';
import { useObjectTypeId } from '../../../v2/hooks/useObjectTypeId';
import { useProperty } from '../../../v2/hooks/useProperty';
import { useIsCrmCommandHandlerAvailable } from '../../../v2/utils/useIsCrmCommandHandlerAvailable';
import { trackDetailsLinkButtonClick } from '../../../utils/usageTrackingUtils';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
const getHistoryHref = (objectTypeId, objectId, label) => `/contacts/${PortalIdParser.get()}/record/${encodeURIComponent(objectTypeId)}/${encodeURIComponent(objectId)}/history?search=${encodeURIComponent(label)}`;

/**
 * This JSX is a hook instead of a component because we need to check conditionally whether the rendered result is truthy/falsy,
 * in order to be able to not pass content to FormControl's hoverContent prop.
 *
 * Were this a component, its 'value' would always be truthy and we would wind up passing empty, but present, hover content.
 */
export const useGetDetailsLinkButton = (displayOptimized, onClick) => {
  const objectId = useObjectId();
  const objectTypeId = useObjectTypeId();
  const property = useProperty();
  const isCrmCommandHandlerAvailable = useIsCrmCommandHandlerAvailable();
  const showPropertyHistoryPanel = useSendCrmMessageTopic(TOPIC_NAMES.OPEN_PROPERTY_HISTORY);
  return useMemo(() => {
    if (!displayOptimized) {
      return null;
    }
    const handleClick = () => {
      trackDetailsLinkButtonClick({
        propertyName: property.name,
        objectTypeId
      });
      if (onClick) {
        onClick();
      } else {
        showPropertyHistoryPanel({
          objectTypeId,
          objectId: String(objectId),
          propertyNames: [property.name]
        });
      }
    };
    if (!isCrmCommandHandlerAvailable && !onClick) {
      return /*#__PURE__*/_jsx(Text, {
        as: "span",
        typography: "detail-100",
        children: /*#__PURE__*/_jsx(Button, {
          use: "link",
          href: getHistoryHref(objectTypeId, String(objectId), property.label),
          external: true,
          hideExternalLinkIcon: true,
          onClick: () => {
            trackDetailsLinkButtonClick({
              propertyName: property.name,
              objectTypeId
            });
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataProperties.formControl.hoverContent"
          })
        })
      });
    }
    return /*#__PURE__*/_jsx(Text, {
      as: "span",
      typography: "detail-100",
      children: /*#__PURE__*/_jsx(Button, {
        use: "link",
        onClick: handleClick,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.formControl.hoverContent"
        })
      })
    });
  }, [displayOptimized, objectId, objectTypeId, property.name, property.label, isCrmCommandHandlerAvailable, onClick, showPropertyHistoryPanel]);
};